import { STARTER_GOLD, M_BY_ID } from "../constants/matches";
import { getStats, baseVitality } from "./itemEngine";

interface PlayerLike {
  equipped?: Record<string, string | null>;
  inventory?: Array<{ iid: string; slot: string; rarity: string; stats?: Record<string, number> }>;
  spent?: number;
  match_gold?: number;
  bonus_points?: number;
  score?: number;      // XP total persisté (pour dériver le niveau → socle de vitalité)
  duel_xp?: number;
}

interface MatchLike {
  id: string;
  p1: number;
  pX: number;
  p2: number;
}

/* ─── Système d'XP / Niveau ───────────────────────────────────────────────────
   L'XP = total des points gagnés (derived.earned). Pas de monnaie séparée.
   Progression illimitée mais ralentie quadratiquement.                         */

export const MAX_LEVEL = 100;

/** XP totale cumulée requise pour atteindre le niveau L (L ≥ 1).
 *  Formule : 8.05 × (L-1)² + 142 × (L-1)  → niveau 1 = 0 XP. */
export const pointsForLevel = (level: number): number => {
  const l = Math.max(1, level) - 1;
  return Math.round(8.05 * l * l + 142 * l);
};

/** Niveau du joueur d'après son XP totale, plafonné à MAX_LEVEL. */
export const playerLevel = (points: number): number => {
  let lv = 1;
  while (lv < MAX_LEVEL && pointsForLevel(lv + 1) <= points) lv++;
  return lv;
};

/** Progression vers le niveau suivant (ratio [0..1] pour la barre XP). */
export function levelProgress(points: number) {
  const level = playerLevel(points);
  const maxed = level >= MAX_LEVEL;
  const cur = pointsForLevel(level);
  const next = maxed ? cur : pointsForLevel(level + 1);
  const span = next - cur;
  const into = points - cur;
  return {
    level,
    into: maxed ? span : into,
    span,
    ratio: maxed ? 1 : span > 0 ? Math.min(1, into / span) : 0,
    nextLevel: maxed ? MAX_LEVEL : level + 1,
    remaining: maxed ? 0 : Math.max(0, next - points),
  };
}

/**
 * Formatage des scores : entier sous 10 000, format "K" (1 décimale) au-delà.
 * 9500 → "9500", 10000 → "10.0K", 10500 → "10.5K".
 */
export const formatScore = (n: number) =>
  n >= 10000 ? `${(n / 1000).toFixed(1)}K` : String(Math.round(n));

export const getBetPoints = (m: { p1: number; pX: number; p2: number }, pick: string): number => {
  const p = pick === "team1" ? m.p1 : pick === "draw" ? m.pX : m.p2;
  return Math.max(5, Math.round(100 / p / 5) * 5);
};

const pickPts = getBetPoints;

export function cStats(player: PlayerLike, level?: number) {
  const lv = level ?? playerLevel((player.score ?? 0) + (player.duel_xp ?? 0));
  const t = {
    technique: 10,
    defense: 10,
    physique: 10,
    vitesse: baseVitality(lv),   // socle de vitalité (HP) selon le niveau
    blessure: 0,
  };
  Object.values(player.equipped || {}).forEach((iid) => {
    const it = (player.inventory || []).find((x) => x.iid === iid);
    if (!it) return;
    const s = getStats(it);
    // N'additionner que les 4 stats connues (+ blessure) : ignore les affixes
    // spéciaux comme `energy` (ballon PVE) qui ne font pas partie du power.
    for (const k in s) if (k in t) t[k as keyof typeof t] += s[k];
  });
  return t;
}

export function cDerived(
  player: PlayerLike,
  bets: Record<string, string>,
  results: Record<string, { o: string; s: string }>,
  predScores: Record<string, { a: string | number; b: string | number }> = {},
  mById: Record<string, MatchLike> = M_BY_ID
) {
  let earned = 0,
    won = 0,
    total = 0;
  Object.entries(bets || {}).forEach(([mid, pick]) => {
    const res = results[mid];
    const m = mById[mid];
    if (res && m) {
      total++;
      if (res.o === pick) {
        let pts = pickPts(m, pick);
        const pred = predScores[mid];
        if (pred && res.s) {
          const [ra, rb] = res.s.split(/[–-]/).map(Number);
          if (
            !isNaN(ra) &&
            !isNaN(rb) &&
            Number(pred.a) === ra &&
            Number(pred.b) === rb
          )
            pts *= 2;
        }
        earned += pts;
        won++;
      }
    }
  });
  // Points crédités hors paris (récompenses manuelles / sandbox) — comptent comme de l'XP/gold.
  earned += player.bonus_points || 0;
  // Niveau dérivé de l'XP live (paris + bonus + duel) → socle de vitalité cohérent.
  const level = playerLevel(earned + (player.duel_xp ?? 0));
  const stats = cStats(player, level);
  return {
    earned,
    won,
    total,
    winrate: total ? won / total : 0,
    wallet:
      STARTER_GOLD + earned + (player.match_gold || 0) - (player.spent || 0),
    stats,
    power:
      stats.technique + stats.defense + stats.physique + stats.vitesse,
  };
}

/* ─── K League simulation engine ─────────────────────────────────────────── */

const BOT_F = [
  "Sergio", "Lucas", "Karim", "Antoine", "Rayan",
  "Théo", "Axel", "Noah", "Enzo", "Kylian",
  "Mohamed", "Alexis", "Nathan", "Quentin", "Florian",
  "Jonathan", "Dylan", "Kevin", "Samir", "Killian",
];
const BOT_L = [
  "Dupont", "Silva", "García", "Müller", "Kim",
  "Park", "Benzema", "Kanté", "Salah", "Gnabry",
  "Bellingham", "Vinícius", "Camavinga", "Thuram", "Dembélé",
  "Giroud", "Lloris", "Varane", "Digne", "Lemar",
];

export const botN = (s: number) =>
  `${BOT_F[s % BOT_F.length]} ${BOT_L[Math.floor(s * 7 + 3) % BOT_L.length]}`;

interface SimPlayer {
  id: string;
  name: string;
  h: boolean;
}

export function wPick(arr: SimPlayer[], hB = 1): SimPlayer {
  const w = arr.map((p) => (p.h ? hB : 1)),
    t = w.reduce((a, b) => a + b, 0);
  let r = Math.random() * t;
  for (let i = 0; i < arr.length; i++) {
    r -= w[i];
    if (r <= 0) return arr[i];
  }
  return arr[arr.length - 1];
}

export function runSim(humanName: string) {
  const human: SimPlayer = { id: "h", name: humanName, h: true };
  const hStart: SimPlayer[] = [
    human,
    ...Array.from({ length: 10 }, (_, i) => ({
      id: `hb${i}`,
      name: botN(i),
      h: false,
    })),
  ];
  const aStart: SimPlayer[] = Array.from({ length: 11 }, (_, i) => ({
    id: `ab${i}`,
    name: botN(i + 20),
    h: false,
  }));
  const hBench: SimPlayer[] = Array.from({ length: 3 }, (_, i) => ({
    id: `hs${i}`,
    name: botN(i + 11),
    h: false,
  }));
  const aBench: SimPlayer[] = Array.from({ length: 3 }, (_, i) => ({
    id: `as${i}`,
    name: botN(i + 31),
    h: false,
  }));
  const home = [...hStart],
    away = [...aStart];
  let hS = 3,
    aS = 3;
  const evts: Record<string, any>[] = [];
  let hG = 0,
    hA = 0,
    hSh = 0,
    hInj = false;

  for (let min = 1; min <= 90; min++) {
    const r = Math.random();
    if (r < 0.032) {
      const isH = Math.random() < 0.5,
        team = isH ? home : away;
      const sc = wPick(team, 3),
        rest = team.filter((p) => p.id !== sc.id);
      const as = Math.random() < 0.78 && rest.length ? wPick(rest, 2) : null;
      evts.push({
        min,
        type: "goal",
        team: isH ? "home" : "away",
        scorer: sc.name,
        assister: as?.name,
        sH: sc.h,
        aH: as?.h,
      });
      if (sc.h) hG++;
      if (as?.h) hA++;
    } else if (r < 0.112) {
      const isH = Math.random() < 0.5,
        sh = wPick(isH ? home : away, 2);
      evts.push({
        min,
        type: "shot",
        team: isH ? "home" : "away",
        player: sh.name,
        isH: sh.h,
      });
      if (sh.h) hSh++;
    } else if (r < 0.134) {
      const doH = Math.random() < 0.6 && hS > 0 && hBench.length > 0;
      if (doH) {
        const out = home.find((p) => !p.h);
        if (out) {
          const inP = hBench.shift()!;
          home.splice(home.indexOf(out), 1, inP);
          hS--;
          evts.push({ min, type: "sub", team: "home", out: out.name, in: inP.name });
        }
      } else if (aS > 0 && aBench.length > 0) {
        const out = away[Math.floor(Math.random() * away.length)];
        const inP = aBench.shift()!;
        away.splice(away.indexOf(out), 1, inP);
        aS--;
        evts.push({ min, type: "sub", team: "away", out: out.name, in: inP.name });
      }
    } else if (r < 0.146) {
      const isH = Math.random() < 0.5,
        inj = wPick(isH ? home : away, 0.3);
      evts.push({
        min,
        type: "injury",
        team: isH ? "home" : "away",
        player: inj.name,
        severity: Math.random() < 0.25 ? "serious" : "minor",
        isH: inj.h,
      });
      if (inj.h) hInj = true;
    }
  }

  const hScore = evts.filter((e) => e.type === "goal" && e.team === "home").length;
  const aScore = evts.filter((e) => e.type === "goal" && e.team === "away").length;

  let penalties = null;
  if (hScore === aScore) {
    const penWin = Math.random() < 0.55 ? "home" : "away";
    const lo = Math.floor(Math.random() * 3) + 3,
      hi = lo + Math.floor(Math.random() * 2) + 1;
    penalties = {
      winner: penWin,
      sh: penWin === "home" ? hi : lo,
      sa: penWin === "away" ? hi : lo,
    };
    evts.push({ min: 90, type: "penalties", winner: penWin, sh: penalties.sh, sa: penalties.sa });
  }

  const finalWin = hScore > aScore || penalties?.winner === "home";
  let rat = 6.0 + hG * 1.5 + hA * 0.8 + hSh * 0.25;
  if (hInj) rat -= 0.5;
  if (finalWin) rat += 0.4;
  else rat -= 0.3;
  if (hG === 0 && hA === 0 && hSh === 0) rat -= 0.8;
  rat = Math.min(10, Math.max(1, Math.round(rat * 10) / 10));

  return {
    evts,
    hScore,
    aScore,
    penalties,
    finalWin,
    hG,
    hA,
    hSh,
    hInj,
    rating: rat,
    hPlayers: hStart.map((p) => p.name),
    aPlayers: aStart.map((p) => p.name),
  };
}
